import { getApiErrorCode, type BookingStatus, type PaymentStatus } from '@cvo/domain';

export enum BOOK_TRIP_STEP {
  SEARCH = 0,
  SELECT = 1,
  REVIEW = 2,
  CUSTOMIZE = 3,
  PAYMENT = 4,
  CONFIRMATION = 5,
}

export const DEFAULT_BOOKING_WINDOW_RANGE = 14;

export const CONFIRMED_PAYMENT_STATUSES: PaymentStatus[] = ['PAID', 'REFUNDED'];

export const TERMINAL_BOOKING_SUCCESS_STATUSES = ['CONFIRMED', 'COMPLETED'] as const satisfies readonly BookingStatus[];

export const CONFIRMABLE_BOOKING_STATUSES = [...TERMINAL_BOOKING_SUCCESS_STATUSES, 'HOLD'] as const satisfies readonly BookingStatus[];

export type ConfirmableBookingStatus = typeof CONFIRMABLE_BOOKING_STATUSES[number];

/** Unlike SETTLED_BOOKING_STATUSES (load-time routing), includes HOLD but not CANCELLED. */
export function isConfirmableBookingStatus(status?: BookingStatus): status is ConfirmableBookingStatus {
  return !!status && (CONFIRMABLE_BOOKING_STATUSES as readonly BookingStatus[]).includes(status);
}

export const SETTLED_BOOKING_STATUSES = ['CONFIRMED', 'CANCELLED', 'COMPLETED'] as const satisfies readonly BookingStatus[];

export type SettledBookingStatus = typeof SETTLED_BOOKING_STATUSES[number];

export function isSettledBookingStatus(status?: BookingStatus): status is SettledBookingStatus {
  return !!status && (SETTLED_BOOKING_STATUSES as readonly BookingStatus[]).includes(status);
}

export const CURRENT_CENTURY = '20';

export const ANCILLARY_INJECTION_ERROR_CODES = [
  'ANCILLARIES_INJECTION_FAILED',
  'INCLUDED_ANCILLARY_NOT_AVAILABLE',
  'CORE-SUBSCRIPTION-INCLUDED-ANCILLARY-NOT-AVAILABLE',
] as const;

/**
 * Returns true if the error is an API error with an ancillary injection failure code.
 */
export function isAncillaryInjectionError(error: unknown): boolean {
  return ANCILLARY_INJECTION_ERROR_CODES.includes(getApiErrorCode(error) as typeof ANCILLARY_INJECTION_ERROR_CODES[number]);
}

/**
 * BFF error code returned by the booking confirmation endpoint when the payment
 * itself failed — a definitive failure, unlike a timeout where the booking may
 * still complete.
 */
export const BOOKING_PAYMENT_FAILED_ERROR_CODE = 'CORE-SUBSCRIPTION-BOOKING-PAYMENT-FAILED';

/**
 * Returns true if the error is an API error signalling a definitive booking
 * payment failure from the confirmation endpoint.
 */
export function isBookingPaymentFailedError(error: unknown): boolean {
  return getApiErrorCode(error) === BOOKING_PAYMENT_FAILED_ERROR_CODE;
}

/**
 * BFF error code returned (422) by the booking payment call when the bank
 * refused the 3-D Secure authentication. `details[payment_authentication].reason` says
 * why (see getApiErrorDetailReason).
 */
export const BOOKING_PAYMENT_AUTHENTICATION_FAILED_ERROR_CODE = 'CORE-SUBSCRIPTION-BOOKING-PAYMENT-AUTHENTICATION-FAILED';

/**
 * Returns true if the error is an API error signalling the 3-D Secure authentication was refused.
 */
export function isBookingPaymentAuthenticationFailedError(error: unknown): boolean {
  return getApiErrorCode(error) === BOOKING_PAYMENT_AUTHENTICATION_FAILED_ERROR_CODE;
}

/**
 * BFF error code returned (400) when the tenant authenticates payments but the
 * payment body carried no `payment_authentication` block.
 */
export const BOOKING_PAYMENT_AUTHENTICATION_REQUIRED_ERROR_CODE = 'CORE-SUBSCRIPTION-BOOKING-PAYMENT-AUTHENTICATION-REQUIRED';

/**
 * Returns true if the error is an API error signalling a missing `payment_authentication` block.
 */
export function isBookingPaymentAuthenticationRequiredError(error: unknown): boolean {
  return getApiErrorCode(error) === BOOKING_PAYMENT_AUTHENTICATION_REQUIRED_ERROR_CODE;
}

export const BOOKING_NOT_FOUND_OR_EXPIRED_ERROR_CODE = 'CORE-SUBSCRIPTION-BOOKING-NOT-FOUND-OR-EXPIRED';

/**
 * Returns true if the error is an API error signalling a booking not found or expired error.
 */
export function isBookingNotFoundOrExpiredError(error: unknown): boolean {
  return getApiErrorCode(error) === BOOKING_NOT_FOUND_OR_EXPIRED_ERROR_CODE;
}

/**
 * BFF error code returned by the quotation endpoint when the selected availability is no
 * longer bookable (the BE re-validates availability at quotation time).
 */
/**
 * The selected availability is no longer bookable. The booking-engine variant arrives when
 * Navitaire reports `NoFareApplied`, which the backend reclassified out of the first code.
 */
export const BOOKING_NOT_AVAILABLE_ERROR_CODES = [
  'CORE-SUBSCRIPTION-BOOKING-NOT-AVAILABLE',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-FLIGHT-NOT-AVAILABLE',
] as const;

/**
 * Returns true if the error is an API error signalling the selected availability is no
 * longer bookable.
 */
export function isBookingNotAvailableError(error: unknown): boolean {
  return BOOKING_NOT_AVAILABLE_ERROR_CODES.includes(getApiErrorCode(error) as typeof BOOKING_NOT_AVAILABLE_ERROR_CODES[number]);
}

/**
 * BFF error code returned by the availability confirmation POST when the search dates fall
 * outside the route's booking window (e.g. the window shifted between the cached browse
 * results and the real-time confirmation).
 */
export const BOOKING_DATE_OUTSIDE_BOOKING_WINDOW_ERROR_CODE = 'CORE-SUBSCRIPTION-BOOKINGS-DATE-OUTSIDE-BOOKING-WINDOW';

/**
 * Returns true if the error is an API error signalling the searched dates are outside the
 * route's booking window.
 */
export function isBookingDateOutsideBookingWindowError(error: unknown): boolean {
  return getApiErrorCode(error) === BOOKING_DATE_OUTSIDE_BOOKING_WINDOW_ERROR_CODE;
}

/**
 * Stable platform error codes core-subscriptions returns on any booking endpoint when the
 * booking engine (Navitaire) rejects the request. One UI treatment per code.
 */
export const BOOKING_ENGINE_ERROR_CODES = [
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-NOT-PERMITTED',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-PASSENGER-DATA-MISMATCH',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-PASSENGER-DATA-INCOMPLETE',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-REJECTED',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-PAYMENT-REJECTED',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-SESSION-EXPIRED',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-BOOKING-WINDOW-CLOSED',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-FLIGHT-NOT-AVAILABLE',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-ROUTE-INVALID',
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-UNAVAILABLE',
] as const;

export type BookingEngineErrorCode = typeof BOOKING_ENGINE_ERROR_CODES[number];

/** The CORE-SUBSCRIPTION-BOOKING-ENGINE-* code carried by the error, if any. */
export function getBookingEngineErrorCode(error: unknown): BookingEngineErrorCode | undefined {
  const code = getApiErrorCode(error);
  return BOOKING_ENGINE_ERROR_CODES.includes(code as BookingEngineErrorCode) ? (code as BookingEngineErrorCode) : undefined;
}

/**
 * True for a definitive booking-engine rejection — every code but the transient
 * CORE-SUBSCRIPTION-BOOKING-ENGINE-UNAVAILABLE. Polling the booking after one of these is pointless.
 */
export function isBookingEngineRejection(error: unknown): boolean {
  const code = getBookingEngineErrorCode(error);
  return !!code && code !== 'CORE-SUBSCRIPTION-BOOKING-ENGINE-UNAVAILABLE';
}

/** Definitive booking failures: neither polling the booking nor retrying the same request can succeed. */
export function isDefinitiveBookingFailure(error: unknown): boolean {
  return isBookingPaymentFailedError(error) || isBookingEngineRejection(error) || isAncillaryInjectionError(error);
}
